/**
 * Lo que un cliente puede pedir de una sección y un template puede o no
 * enseñar. El titular no está: todos lo tienen.
 */
export const SECTION_FIELDS = ['eyebrow', 'subheadline', 'primaryAction', 'secondaryAction', 'items', 'image'];
/** La marca de la fila de botones de una sección, para el editor de templates. */
export const ACTIONS_ZONE_SLOT = 'group.actions';
const slotOf = (node) => (typeof node.aiSlot === 'string' ? node.aiSlot.trim() : '');
const baseOf = (slot) => slot.replace(/\.\d+$/, '');
const indexOf = (slot) => Number(/\.(\d+)$/.exec(slot)?.[1] ?? 0);
const isButtonNode = (node) => node.type === 'button' || node.type === 'link';
const ACTION_BASES = new Set(['action.primary', 'action.secondary', 'action.tertiary', 'action.link', 'slot-cta']);
const isPrimarySlot = (slot) => slot === 'action.primary' || slot === 'slot-cta';
/** Lo de dentro de una lista repetible es de cada elemento, no de la sección. */
const REPEAT_SLOTS = new Set(['repeat.list', 'repeat.item', 'slot-list']);
/** Recorre lo que es de la sección: sin entrar en las listas repetibles. */
function walkSection(elements, visit) {
    const go = (node, parent) => {
        if (visit(node, parent) === true)
            return true;
        if (REPEAT_SLOTS.has(baseOf(slotOf(node))))
            return false;
        return node.children.some((child) => go(child, node));
    };
    elements.some((root) => go(root, null));
}
function desktop(node) {
    return node.styles?.desktop ?? {};
}
/** Si un contenedor apila a sus hijos: flex en columna o un bloque normal. Nunca una rejilla ni una fila. */
function flowsInColumn(node) {
    if (!node)
        return false;
    const styles = desktop(node);
    const display = typeof styles.display === 'string' ? styles.display : '';
    if (display.includes('grid'))
        return false;
    if (display.includes('flex'))
        return styles.flexDirection === 'column' || styles.flexDirection === 'column-reverse';
    return true;
}
/** Un contenedor de la sección cuyos hijos son todos botones, y alguno de la sección. */
function isButtonRow(node) {
    if (node.type !== 'div' || !node.children.length)
        return false;
    return node.children.every(isButtonNode) && node.children.some((child) => ACTION_BASES.has(baseOf(slotOf(child))));
}
/** La zona de acciones que ya existe: marcada o una fila de botones. */
export function actionsZoneOf(elements) {
    let marked = null;
    let row = null;
    walkSection(elements, (node) => {
        if (slotOf(node) === ACTIONS_ZONE_SLOT) {
            marked = node;
            return true;
        }
        if (!row && isButtonRow(node))
            row = node;
        return false;
    });
    return marked ?? row;
}
/** El botón principal de la sección y su contenedor. */
function primaryOf(elements) {
    let found = null;
    walkSection(elements, (node, parent) => {
        if (isButtonNode(node) && isPrimarySlot(slotOf(node))) {
            found = { node, parent };
            return true;
        }
        return false;
    });
    return found;
}
/** El titular de la sección (el primero) y su contenedor. */
function headlineOf(elements) {
    let found = null;
    walkSection(elements, (node, parent) => {
        const slot = slotOf(node);
        if ((baseOf(slot) === 'headline' && indexOf(slot) <= 1) || slot === 'slot-title') {
            found = { node, parent };
            return true;
        }
        return false;
    });
    return found;
}
function hasSectionSlot(elements, test) {
    let hit = false;
    walkSection(elements, (node) => {
        const slot = slotOf(node);
        if (slot && test(baseOf(slot), indexOf(slot))) {
            hit = true;
            return true;
        }
        return false;
    });
    return hit;
}
function hasAny(elements, test) {
    const go = (node) => test(node) || node.children.some(go);
    return elements.some(go);
}
/** La forma de un elemento: los slots de dentro, en orden y sin número. */
export function shapeOf(node) {
    const bases = [];
    const walk = (current) => {
        const base = baseOf(slotOf(current));
        if (base && base !== 'repeat.list' && base !== 'slot-list' && base !== 'repeat.item')
            bases.push(base);
        current.children.forEach(walk);
    };
    node.children.forEach(walk);
    return bases.join('|');
}
/**
 * Los elementos que el template puso FUERA de su lista, con la misma forma
 * que los de dentro.
 *
 * «Seis razones alrededor de una foto»: tres a la izquierda —la lista— y tres
 * a la derecha, sueltos. Sólo se llenaba la lista; a los de la derecha les
 * tocaban, por número, los textos de los primeros, desfasados: el título del
 * primero sin texto, el del segundo con el texto del primero… Aquí se ven como
 * lo que son, la continuación de la lista: van después y, si no hay elementos
 * para ellos, se quitan.
 */
export function overflowItemGroups(elements) {
    let list = null;
    const findList = (node) => {
        if (list)
            return;
        if ((slotOf(node) === 'repeat.list' || slotOf(node) === 'slot-list') && node.children.length) {
            list = node;
            return;
        }
        node.children.forEach(findList);
    };
    elements.forEach(findList);
    const found = list;
    const prototype = found?.children[0];
    const shape = prototype ? shapeOf(prototype) : '';
    if (!found || !shape || !shape.includes('headline'))
        return null;
    /* El titular de la sección —el primero fuera de la lista— no es de ningún
       elemento: un encabezado de «titular + párrafo» tiene la misma forma que un
       elemento de «titular + texto», y sin esto se lo habría llevado uno. */
    let title = null;
    const findTitle = (node) => {
        if (title || node === found)
            return;
        const base = baseOf(slotOf(node));
        if (base === 'headline' || base === 'slot-title') {
            title = node;
            return;
        }
        node.children.forEach(findTitle);
    };
    elements.forEach(findTitle);
    const contains = (node, target) => Boolean(target) && (node === target || node.children.some((child) => contains(child, target)));
    const groups = [];
    const walk = (node) => {
        if (node === found)
            return;
        if (!slotOf(node) && node.children.length && shapeOf(node) === shape && !contains(node, title)) {
            groups.push(node);
            return;
        }
        node.children.forEach(walk);
    };
    elements.forEach(walk);
    return groups.length ? { list: found, groups } : null;
}
const ITEM_BASES = ['stat.value', 'quote', 'question', 'price.value', 'author'];
export function itemCapacity(elements) {
    let list = null;
    let behaviorCount = -1;
    const counts = new Map();
    let headlines = 0;
    const go = (node) => {
        const slot = slotOf(node);
        const base = baseOf(slot);
        const behavior = (node.behavior && typeof node.behavior === 'object' ? node.behavior : {});
        if (behavior.type === 'faq' || behavior.type === 'reviews') {
            const rows = behavior.type === 'faq' ? behavior.items : behavior.reviews;
            behaviorCount = Math.max(behaviorCount, Array.isArray(rows) ? rows.length : 0);
        }
        /* Un catálogo (productos, servicios) dibuja las ofertas de verdad del
           sitio: los elementos que escriba la IA no lo llenan, pero tampoco le
           faltan. Cuenta como lista. */
        const repeat = (node.cmsRepeat && typeof node.cmsRepeat === 'object' ? node.cmsRepeat : null);
        if (repeat)
            behaviorCount = Math.max(behaviorCount, typeof repeat.limit === 'number' && repeat.limit > 0 ? repeat.limit : 3);
        if (!list && (base === 'repeat.list' || base === 'slot-list')) {
            list = node;
            return;
        }
        if (base === 'headline' || base === 'slot-title')
            headlines += 1;
        if (ITEM_BASES.includes(base))
            counts.set(base, (counts.get(base) ?? 0) + 1);
        node.children.forEach(go);
    };
    elements.forEach(go);
    if (list) {
        /* Con elementos también fuera de la lista (seis alrededor de una foto), las
           casillas son fijas: la lista y los de fuera. Ver `overflowItemGroups`. */
        const overflow = overflowItemGroups(elements);
        if (overflow)
            return { designed: list.children.length + overflow.groups.length, flexible: false };
        return { designed: Math.max(1, list.children.length), flexible: true };
    }
    if (behaviorCount >= 0)
        return { designed: Math.max(1, behaviorCount), flexible: true };
    /* El primer titular es el de la sección; los demás, uno por elemento. */
    const designed = Math.max(headlines - 1, ...counts.values(), 0);
    return designed > 0 ? { designed, flexible: false } : null;
}
/**
 * Lo que un template enseña tal cual (`shows`) y lo que se le puede agregar
 * (`addable`). La elección prefiere el que lo enseña; si no hay, el que lo
 * admite; y sólo si ninguno, queda registrado como pedido sin cumplir.
 */
export function sectionFieldReach(elements) {
    const shows = new Set();
    if (hasSectionSlot(elements, (base) => base === 'eyebrow' || base === 'slot-eyebrow'))
        shows.add('eyebrow');
    /* Un primer párrafo bajo el titular ya hace de subtítulo (`applySectionCopy`
       le pone el subtítulo cuando no hay slot propio): agregar otro lo repetiría. */
    if (hasSectionSlot(elements, (base, index) => base === 'subheadline' || base === 'slot-subtitle' || ((base === 'body' || base === 'slot-body') && index <= 1)))
        shows.add('subheadline');
    if (primaryOf(elements))
        shows.add('primaryAction');
    if (hasSectionSlot(elements, (base) => base === 'action.secondary' || base === 'action.link'))
        shows.add('secondaryAction');
    if (hasAny(elements, (node) => {
        const base = baseOf(slotOf(node));
        const behavior = (node.behavior && typeof node.behavior === 'object' ? node.behavior : {});
        return REPEAT_SLOTS.has(base) || base === 'stat.value' || base === 'question'
            || ((base === 'headline' || base === 'body') && indexOf(slotOf(node)) >= 2)
            || behavior.type === 'faq' || behavior.type === 'reviews';
    }))
        shows.add('items');
    if (hasAny(elements, (node) => node.type === 'image' && !/^media\.(logo|avatar)/.test(slotOf(node))))
        shows.add('image');
    const addable = new Set();
    const headline = headlineOf(elements);
    if (headline && flowsInColumn(headline.parent)) {
        if (!shows.has('eyebrow'))
            addable.add('eyebrow');
        if (!shows.has('subheadline'))
            addable.add('subheadline');
    }
    const primary = primaryOf(elements);
    if (primary && !shows.has('secondaryAction') && (actionsZoneOf(elements) || flowsInColumn(primary.parent)))
        addable.add('secondaryAction');
    return { shows: SECTION_FIELDS.filter((field) => shows.has(field)), addable: SECTION_FIELDS.filter((field) => addable.has(field)) };
}
// ── Agregar ──────────────────────────────────────────────────────────────────
const ALIGN_TO_JUSTIFY = { center: 'center', 'flex-end': 'flex-end', end: 'flex-end', right: 'flex-end' };
/**
 * La fila nueva para un botón suelto, alineada como lo estaba él: si su columna
 * lo centraba (o centraba el texto), la fila centra; si no, empieza a la
 * izquierda. Por tamaño, porque un hero centrado en escritorio puede ir a la
 * izquierda en el teléfono.
 */
function zoneStylesFor(parent) {
    const styles = {};
    const parentStyles = parent.styles ?? {};
    for (const bp of ['desktop', 'tablet', 'mobile']) {
        const bag = parentStyles[bp] ?? {};
        const align = typeof bag.alignItems === 'string' ? bag.alignItems : typeof bag.textAlign === 'string' ? bag.textAlign : '';
        const justify = align ? (ALIGN_TO_JUSTIFY[align] ?? 'flex-start') : undefined;
        if (bp === 'desktop') {
            styles.desktop = { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: '12px', justifyContent: justify ?? 'flex-start' };
        }
        else if (justify) {
            styles[bp] = { justifyContent: justify };
        }
    }
    return styles;
}
/** Envuelve el botón principal suelto en una fila nueva, en su mismo sitio. */
function wrapPrimary(found, cloneNode) {
    const parent = found.parent;
    if (!parent)
        return null;
    const at = parent.children.indexOf(found.node);
    if (at < 0)
        return null;
    /* Sin nombre propio: en Capas sale como cualquier contenedor, en el idioma
       de quien edita, y con la marca de zona en el selector de la IA. */
    const zone = cloneNode({ type: 'div', aiSlot: ACTIONS_ZONE_SLOT, styles: zoneStylesFor(parent), children: [] });
    zone.children = [found.node];
    parent.children.splice(at, 1, zone);
    return zone;
}
/**
 * Deja la zona de acciones marcada en el template, para guardarlo así: la fila
 * de botones que ya había recibe la marca, y el botón principal suelto en una
 * columna se envuelve en su fila. Es lo mismo que el reparto hace al vuelo
 * cuando hace falta (`addRequestedParts`), pero a la vista de quien edita el
 * template. No cambia cómo se ve: la fila nueva se alinea como estaba el botón.
 *
 * Devuelve qué hizo: `marked` (ya había fila), `wrapped` (se creó) o nada si ya
 * estaba marcada, no hay botón principal o su contenedor no apila.
 */
export function ensureActionsZone(elements, cloneNode) {
    let marked = false;
    walkSection(elements, (node) => {
        if (slotOf(node) === ACTIONS_ZONE_SLOT) {
            marked = true;
            return true;
        }
        return false;
    });
    if (marked)
        return null;
    const row = actionsZoneOf(elements);
    if (row) {
        row.aiSlot = ACTIONS_ZONE_SLOT;
        return 'marked';
    }
    const primary = primaryOf(elements);
    if (!primary || !flowsInColumn(primary.parent))
        return null;
    return wrapPrimary(primary, cloneNode) ? 'wrapped' : null;
}
/** Lo que era sólo del botón original y no debe pasar al nuevo. */
const BUTTON_ONLY_KEYS = ['iconName', 'icon', 'iconPosition', 'iconSide', 'iconOnly', 'anchorId', 'label', 'interaction', 'contentBinding'];
const FIXED_SIZE_KEYS = ['width', 'minWidth', 'maxWidth'];
function cleanSecondary(button, text) {
    for (const key of BUTTON_ONLY_KEYS)
        delete button[key];
    /* Un ancho fijo era para el texto del principal; `fit-content` y `auto` sí se
       quedan, que es lo que deja al botón medir lo que su texto. */
    for (const bag of Object.values(button.styles ?? {})) {
        if (!bag)
            continue;
        for (const key of FIXED_SIZE_KEYS) {
            const value = bag[key];
            if (typeof value === 'string' && value !== 'fit-content' && value !== 'auto')
                delete bag[key];
        }
    }
    const refs = (button.styleRefs && typeof button.styleRefs === 'object' ? button.styleRefs : {});
    button.styleRefs = { ...refs, button: 'button-secondary' };
    button.themeRole = 'buttonSecondary';
    button.aiSlot = 'action.secondary';
    button.href = '#';
    button.content = text;
    return button;
}
function addSecondaryAction(elements, text, cloneNode) {
    const primary = primaryOf(elements);
    if (!primary)
        return null;
    let zone = actionsZoneOf(elements);
    if (!zone) {
        if (!flowsInColumn(primary.parent))
            return null;
        zone = wrapPrimary(primary, cloneNode);
        if (!zone)
            return null;
    }
    const secondary = cleanSecondary(cloneNode(primary.node), text);
    const at = zone.children.indexOf(primary.node);
    zone.children.splice(at >= 0 ? at + 1 : zone.children.length, 0, secondary);
    return secondary;
}
/** Un texto nuevo del encabezado, con la tipografía global de su papel y la alineación del titular. */
function headingText(headline, slot, text, cloneNode) {
    const align = desktop(headline).textAlign;
    const node = cloneNode({
        type: 'text',
        aiSlot: slot,
        content: text,
        styleRefs: { typography: slot === 'eyebrow' ? 'small' : 'p' },
        themeRole: slot === 'eyebrow' ? 'eyebrow' : 'paragraph',
        themeStyleScope: 'global',
        styles: { desktop: { margin: '0', maxWidth: slot === 'subheadline' ? '640px' : '100%', ...(typeof align === 'string' ? { textAlign: align } : {}) } },
        children: [],
    });
    return node;
}
function addHeadingPart(elements, slot, text, cloneNode) {
    const headline = headlineOf(elements);
    if (!headline?.parent || !flowsInColumn(headline.parent))
        return null;
    const at = headline.parent.children.indexOf(headline.node);
    if (at < 0)
        return null;
    const node = headingText(headline.node, slot, text, cloneNode);
    headline.parent.children.splice(slot === 'eyebrow' ? at : at + 1, 0, node);
    return node;
}
/**
 * Agrega a la sección lo que el cliente pidió expresamente y el template no
 * trae, donde las zonas lo permiten. Devuelve lo que agregó, ya con su texto:
 * quien llama lo da por escrito para que no se tome por texto de muestra.
 */
export function addRequestedParts(elements, copy, cloneNode) {
    const must = new Set(copy.must ?? []);
    if (!must.size)
        return [];
    const { shows } = sectionFieldReach(elements);
    const added = [];
    if (must.has('secondaryAction') && copy.secondaryAction && !shows.includes('secondaryAction')) {
        const node = addSecondaryAction(elements, copy.secondaryAction, cloneNode);
        if (node)
            added.push(node);
    }
    if (must.has('eyebrow') && copy.eyebrow && !shows.includes('eyebrow')) {
        const node = addHeadingPart(elements, 'eyebrow', copy.eyebrow, cloneNode);
        if (node)
            added.push(node);
    }
    if (must.has('subheadline') && copy.subheadline && !shows.includes('subheadline')) {
        const node = addHeadingPart(elements, 'subheadline', copy.subheadline, cloneNode);
        if (node)
            added.push(node);
    }
    return added;
}
//# sourceMappingURL=templateZones.js.map